"use client";

/**
 * Custom sandboxed interactives showcase: the editor pane shows the exact
 * source strings that the JsInteractive iframe beside it executes — what
 * visitors read is literally what runs.
 */

import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import JsInteractive from "@/components/interactives/JsInteractive";
import Reveal from "./Reveal";
import { DEMO_WIDGET } from "./demoContent";

const FILES = [
  { key: "html", name: "widget.html", code: DEMO_WIDGET.html },
  { key: "css", name: "widget.css", code: DEMO_WIDGET.css },
  { key: "js", name: "widget.js", code: DEMO_WIDGET.js },
] as const;

export default function JsShowcase() {
  const [file, setFile] = useState<(typeof FILES)[number]["key"]>("js");
  const active = FILES.find((f) => f.key === file)!;

  return (
    <section id="custom" className="scroll-mt-16 bg-[var(--ld-paper)]">
      <div className="mx-auto max-w-6xl px-6 py-20 lg:py-24">
        <Reveal>
          <p className="ld-eyebrow">Custom interactives</p>
          <h2 className="ld-display mt-2 text-2xl font-bold sm:text-3xl">
            When the built-ins aren&apos;t enough, build your own
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-muted">
            Write plain HTML, CSS, and JavaScript — or describe the widget and let
            the authoring assistant write it — and it runs as an activity right on
            the lesson page. Simulations, explorable diagrams, little games:
            whatever the lesson needs.
          </p>
        </Reveal>

        <Reveal index={1}>
          <div className="mt-10 grid items-start gap-6 lg:grid-cols-2">
            <div className="ld-editor">
              <div className="ld-editor-bar">
                <span className="ld-editor-dot" />
                <span className="ld-editor-dot" />
                <div role="tablist" aria-label="Widget source files" className="ml-3 flex">
                  {FILES.map((f) => (
                    <button
                      key={f.key}
                      role="tab"
                      aria-selected={file === f.key}
                      onClick={() => setFile(f.key)}
                      className="ld-editor-tab"
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              </div>
              {/* Scrollable region: focusable so keyboard users can scroll the
                  code (axe flags scrollable regions with NO tabindex; the
                  jsx-a11y rule doesn't know about labelled regions) */}
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <pre className="ld-editor-code" role="region" aria-label={`Source of ${active.name}`} tabIndex={0}>
                <code>{active.code}</code>
              </pre>
            </div>

            <div>
              <JsInteractive html={DEMO_WIDGET.html} css={DEMO_WIDGET.css} js={DEMO_WIDGET.js} />
              <p className="mt-1 text-xs text-faint">
                This is that exact code, running live — drag the slider.
              </p>
              <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-line bg-surface px-4 py-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ok)]" />
                <p className="text-sm leading-relaxed text-muted">
                  Every widget runs in an <span className="font-medium text-foreground">origin-isolated
                  sandbox</span>{" "}
                  (<code className="rounded bg-accent-soft px-1 font-[family-name:var(--ld-font-mono)] text-xs">
                    sandbox=&quot;allow-scripts&quot;
                  </code>
                  , no same-origin): it can draw, animate, and respond to input, but it can
                  never read student sessions, call the API, or reach into Canvas.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
